// ============================================================================
// ENUMS
// ============================================================================

export enum LessonType {
  VIDEO = 'video',
  QUIZ = 'quiz',
  GAME = 'game'
}

export enum CompletedType {
  MANUAL = 'manual',        // Marked complete manually by admin
  AUTO = 'auto'             // System auto-complete (default)
}

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * LessonResponse - Audit trail for each quiz/game response
 * Note: This array is always empty when fetching from API
 * Only used for writing response history to API for audit purposes
 */
export interface LessonResponse {
  lessonId: string;              // Matches Lesson.lessonId
  lessonType: LessonType;        // QUIZ or GAME only
  itemNumber: number;            // Question number for quiz, scenario number for game
  correctResponse: boolean;      // Whether the response was correct/excellent/good
  responseAt: string | null;     // ISO 8601 UTC timestamp when response was submitted
  response: string;              // The actual response (option ID, choice letter, etc.)
  attempt: number;               // Which attempt this response belongs to
  impactScore?: number;          // Impact score for game responses
}

/**
 * Lesson - Tracks progress for a single lesson within a course
 * Note: All *At timestamps are ISO 8601 format in UTC
 * LessonId format:
 *   - Videos: "00000000-0000-0000-0000-{order padded to 12 digits}" (e.g., "00000000-0000-0000-0000-000000000001")
 *   - Quiz: matches course.quizId UUID
 *   - Game: matches course.gameId UUID
 */
export interface Lesson {
  lessonId: string;              // UUID format for all lesson types
  lessonType: LessonType;
  startedAt: string | null;      // ISO 8601 UTC timestamp - set on first play/start
  completedAt: string | null;    // ISO 8601 UTC timestamp - set when completed
  completedType: CompletedType | null;
  currentPositionTime: string;   // HH:MM:SS format (elapsed time)
  currentPositionIndex: number;  // Current position in seconds
  totalResponses: number;        // For quiz/game lessons (not used for video)
  correctResponses: number;      // For quiz/game lessons (not used for video)
  durationSpent: number;         // Total seconds spent (cumulative)
  attempt: number;               // Session attempt counter
  progress: number;              // 0-100 percentage
}

/**
 * UserCourse - Tracks overall progress for a user in a course
 * Contains array of Lessons for detailed progress tracking
 */
export interface UserCourse {
  id: string;
  profileId: string;
  courseId: string;
  certificationId: string;       // Certification this course belongs to
  progress: number;              // Overall course progress (0-100)
  lessonsCompleted: number;      // Number of completed lessons
  lastAccessedAt: string;        // ISO 8601 UTC
  lastAccessedLesson: LessonType | null;  // Type of last accessed lesson
  lastAccessedLessonId: string | null;    // ID of last accessed lesson
  userLessons: Lesson[];         // Array of lesson progress
  lessonResponses: LessonResponse[];  // Audit trail - always empty from API, only written to API
}

/**
 * VideoProgress - Internal type for video component use
 * Maps from Lesson data structure for video-specific tracking
 * Note: userId removed as UserCourse carries profileId
 */
export interface VideoProgress {
  videoId: string;               // Maps to Lesson.LessonId
  currentPosition: number;       // Seconds - maps to Lesson.CurrentPositionIndex
  duration: number;              // Total video duration in seconds
  percentComplete: number;       // 0-100 - maps to Lesson.Progress
  isComplete: boolean;           // Derived from Lesson.CompletedAt !== null
  lastWatched: string;           // ISO timestamp
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Converts seconds to HH:MM:SS format
 * @param seconds - Number of seconds
 * @returns Time string in HH:MM:SS format
 */
export function secondsToHHMMSS(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Converts HH:MM:SS format to seconds
 * @param timeString - Time in HH:MM:SS format
 * @returns Total seconds
 */
export function HHMMSSToSeconds(timeString: string): number {
  const parts = timeString.split(':').map(Number);
  if (parts.length !== 3) return 0;
  const [hours, minutes, seconds] = parts;
  return hours * 3600 + minutes * 60 + seconds;
}

// ============================================================================
// MAPPING FUNCTIONS
// ============================================================================

/**
 * Maps from Lesson to VideoProgress for video component use
 * @param lesson - Lesson object for a video
 * @param duration - Total video duration in seconds (from Vimeo player)
 * @returns VideoProgress object or null if lesson is not a video type
 */
export function getVideoProgress(
  lesson: Lesson | undefined,
  duration: number
): VideoProgress | null {
  if (!lesson || lesson.lessonType !== LessonType.VIDEO) {
    return null;
  }

  // Calculate current position
  const currentPosition = lesson.currentPositionIndex;

  // Determine if complete - check for both null and undefined
  const isComplete = lesson.completedAt != null && lesson.completedAt !== '';

  // Use most recent timestamp
  const lastWatched = lesson.completedAt || lesson.startedAt || new Date().toISOString();

  return {
    videoId: lesson.lessonId,
    currentPosition,
    duration,
    percentComplete: lesson.progress,
    isComplete,
    lastWatched
  };
}

/**
 * Updates a Lesson object from VideoProgress data
 * Handles StartedAt, CompletedAt, DurationSpent, and Attempt logic
 *
 * @param lesson - Existing Lesson object or undefined for new lesson
 * @param videoProgress - Current video progress from player
 * @param videoId - Video ID
 * @param completionThreshold - Percentage to consider complete (default 90)
 * @param sessionStart - Start time of current session (for duration calculation)
 * @returns Updated Lesson object
 */
export function updateVideoLesson(
  lesson: Lesson | undefined,
  videoProgress: VideoProgress,
  videoId: string,
  completionThreshold: number = 90,
  sessionStart: Date
): Lesson {
  const now = new Date().toISOString();
  const isNewLesson = !lesson;

  // Calculate session duration
  const sessionDuration = Math.floor((new Date().getTime() - sessionStart.getTime()) / 1000);

  // Determine if video meets completion threshold
  const meetsThreshold = videoProgress.percentComplete >= completionThreshold;

  // Calculate currentPositionTime (elapsed time)
  const currentPositionTime = secondsToHHMMSS(videoProgress.currentPosition);

  return {
    lessonId: videoId,
    lessonType: LessonType.VIDEO,

    // Set startedAt on first play
    startedAt: lesson?.startedAt || now,

    // Set completedAt when threshold reached
    completedAt: meetsThreshold ? (lesson?.completedAt || now) : lesson?.completedAt || null,

    // Set completion type when completed
    completedType: meetsThreshold
      ? (lesson?.completedType || CompletedType.AUTO)
      : lesson?.completedType || null,

    currentPositionTime: currentPositionTime,
    currentPositionIndex: videoProgress.currentPosition,

    // Not used for video lessons
    totalResponses: 0,
    correctResponses: 0,

    // Accumulate duration spent
    durationSpent: (lesson?.durationSpent || 0) + sessionDuration,

    // Increment attempt for new browser session
    attempt: isNewLesson ? 1 : (lesson.attempt || 1),

    // When threshold is met, send 100 so course progress and backend both see lesson as complete (matches game/quiz behavior)
    progress: meetsThreshold ? 100 : videoProgress.percentComplete
  };
}

// ============================================================================
// QUIZ PROGRESS TRACKING
// ============================================================================

/**
 * QuizProgress - Internal type for quiz component use
 * Maps from Lesson data structure for quiz-specific tracking
 */
export interface QuizProgress {
  quizId: string;
  currentQuestionIndex: number;       // Next unanswered question (0-based)
  totalResponses: number;             // Total questions answered
  correctResponses: number;           // Count of correct answers
  percentCorrect: number;             // 0-100 percentage
  isComplete: boolean;                // Quiz completed
  attempt: number;                    // Current attempt number
  lastAttempted: string;              // ISO timestamp
}

/**
 * Maps a Lesson object to QuizProgress for component use
 * @param lesson - Lesson object for a quiz
 * @returns QuizProgress object or null if lesson is not a quiz type
 */
export function getQuizProgress(
  lesson: Lesson | undefined
): QuizProgress | null {
  if (!lesson || lesson.lessonType !== LessonType.QUIZ) {
    return null;
  }

  // Determine if complete - check for both null and undefined
  const isComplete = lesson.completedAt != null && lesson.completedAt !== '';

  // Use most recent timestamp
  const lastAttempted = lesson.completedAt || lesson.startedAt || new Date().toISOString();

  return {
    quizId: lesson.lessonId,
    currentQuestionIndex: lesson.currentPositionIndex,
    totalResponses: lesson.totalResponses,
    correctResponses: lesson.correctResponses,
    percentCorrect: lesson.progress,
    isComplete,
    attempt: lesson.attempt,
    lastAttempted
  };
}

/**
 * Updates a Lesson object from QuizProgress data
 * Handles StartedAt, CompletedAt, DurationSpent, and Attempt logic
 * @param lesson - Existing lesson or undefined for new lesson
 * @param quizProgress - Current quiz progress data
 * @param quizId - The quiz ID (matches course.quizId)
 * @param totalQuestions - Total number of questions in the quiz
 * @param sessionStart - When the current quiz session started
 * @returns Updated Lesson object
 */
export function updateQuizLesson(
  lesson: Lesson | undefined,
  quizProgress: QuizProgress,
  quizId: string,
  totalQuestions: number,
  sessionStart: Date
): Lesson {
  const now = new Date().toISOString();
  const isNewLesson = !lesson;

  // Calculate session duration
  const sessionDuration = Math.floor((new Date().getTime() - sessionStart.getTime()) / 1000);

  // Determine if quiz is completed (all questions answered correctly)
  const isCompleted = quizProgress.isComplete;

  return {
    lessonId: quizId,
    lessonType: LessonType.QUIZ,

    // Set startedAt on first question answered
    startedAt: lesson?.startedAt || now,

    // Set completedAt when quiz is finished with 100% correct
    completedAt: isCompleted ? now : null,

    // Set completion type when completed
    completedType: isCompleted ? CompletedType.AUTO : null,

    // Not used for quiz lessons
    currentPositionTime: '00:00:00',

    // Track next unanswered question
    currentPositionIndex: quizProgress.currentQuestionIndex,

    // Track quiz responses
    totalResponses: quizProgress.totalResponses,
    correctResponses: quizProgress.correctResponses,

    // Accumulate duration spent
    durationSpent: (lesson?.durationSpent || 0) + sessionDuration,

    // Increment attempt when retrying
    attempt: quizProgress.attempt,

    // Track progress as percentage correct
    progress: quizProgress.percentCorrect
  };
}

/**
 * Creates a new Lesson object for a quiz that hasn't been started
 * @param quizId - The quiz ID
 * @returns New Lesson object initialized with defaults
 */
export function createNewQuizLesson(quizId: string): Lesson {
  return {
    lessonId: quizId,
    lessonType: LessonType.QUIZ,
    startedAt: null,
    completedAt: null,
    completedType: null,
    currentPositionTime: '00:00:00',
    currentPositionIndex: 0,
    totalResponses: 0,
    correctResponses: 0,
    durationSpent: 0,
    attempt: 1,
    progress: 0
  };
}

// ============================================================================
// GAME PROGRESS TRACKING
// ============================================================================

/**
 * GameProgress - Internal type for game component use
 * Maps from Lesson data structure for game-specific tracking
 */
export interface GameProgress {
  gameId: string;
  currentScenarioIndex: number;        // Next unanswered scenario (0-based)
  totalResponses: number;               // Total scenarios answered
  correctResponses: number;             // Count of excellent/good choices
  score: number;                        // Total impact score
  percentComplete: number;              // 0-100 percentage
  isComplete: boolean;                  // Game completed
  attempt: number;                      // Current attempt number
  lastAttempted: string;                // ISO timestamp
}

/**
 * Maps a Lesson object to GameProgress for component use
 * @param lesson - Lesson object for a game
 * @returns GameProgress object or null if lesson is not a game type
 */
export function getGameProgress(
  lesson: Lesson | undefined
): GameProgress | null {
  if (!lesson || lesson.lessonType !== LessonType.GAME) {
    return null;
  }

  // Determine if complete - check for both null and undefined
  const isComplete = lesson.completedAt != null && lesson.completedAt !== '';

  // Use most recent timestamp
  const lastAttempted = lesson.completedAt || lesson.startedAt || new Date().toISOString();

  return {
    gameId: lesson.lessonId,
    currentScenarioIndex: lesson.currentPositionIndex,
    totalResponses: lesson.totalResponses,
    correctResponses: lesson.correctResponses,
    score: lesson.progress, // Store score in progress field
    percentComplete: lesson.progress,
    isComplete,
    attempt: lesson.attempt,
    lastAttempted
  };
}

/**
 * Updates a Lesson object from GameProgress data
 * Handles StartedAt, CompletedAt, DurationSpent, and Attempt logic
 * @param lesson - Existing lesson or undefined for new lesson
 * @param gameProgress - Current game progress data
 * @param gameId - The game ID (matches course.gameId)
 * @param totalScenarios - Total number of scenarios in the game
 * @param sessionStart - When the current game session started
 * @returns Updated Lesson object
 */
export function updateGameLesson(
  lesson: Lesson | undefined,
  gameProgress: GameProgress,
  gameId: string,
  totalScenarios: number,
  sessionStart: Date
): Lesson {
  const now = new Date().toISOString();
  const isNewLesson = !lesson;

  // Calculate session duration
  const sessionDuration = Math.floor((new Date().getTime() - sessionStart.getTime()) / 1000);

  // Determine if game is completed (all scenarios answered)
  const isCompleted = gameProgress.isComplete;

  return {
    lessonId: gameId,
    lessonType: LessonType.GAME,

    // Set startedAt on first scenario answered
    startedAt: lesson?.startedAt || now,

    // Set completedAt when game is finished
    completedAt: isCompleted ? (lesson?.completedAt || now) : lesson?.completedAt || null,

    // Set completion type when completed
    completedType: isCompleted
      ? (lesson?.completedType || CompletedType.AUTO)
      : lesson?.completedType || null,

    // Not used for game lessons
    currentPositionTime: '00:00:00',

    // Track next unanswered scenario
    currentPositionIndex: gameProgress.currentScenarioIndex,

    // Track game responses
    totalResponses: gameProgress.totalResponses,
    correctResponses: gameProgress.correctResponses,

    // Accumulate duration spent
    durationSpent: (lesson?.durationSpent || 0) + sessionDuration,

    // Increment attempt when retrying
    attempt: gameProgress.attempt,

    // Track progress as score
    progress: gameProgress.score
  };
}

/**
 * Creates a new Lesson object for a game that hasn't been started
 * @param gameId - The game ID
 * @returns New Lesson object initialized with defaults
 */
export function createNewGameLesson(gameId: string): Lesson {
  return {
    lessonId: gameId,
    lessonType: LessonType.GAME,
    startedAt: null,
    completedAt: null,
    completedType: null,
    currentPositionTime: '00:00:00',
    currentPositionIndex: 0,
    totalResponses: 0,
    correctResponses: 0,
    durationSpent: 0,
    attempt: 1,
    progress: 0
  };
}

// ============================================================================
// COURSE PROGRESS CALCULATION
// ============================================================================

/**
 * Calculates overall course progress and lessons completed count
 * @param userLessons - Array of all lessons in the course
 * @param totalSteps - Total number of steps/lessons in the course (from course.steps)
 * @returns Object with progress (0-100) and lessonsCompleted count
 */
export function calculateCourseProgress(
  userLessons: Lesson[],
  totalSteps: number
): { progress: number; lessonsCompleted: number } {
  if (totalSteps === 0) {
    return { progress: 0, lessonsCompleted: 0 };
  }

  // Each lesson's contribution to overall progress
  const lessonWeight = 100 / totalSteps;

  let totalProgress = 0;
  let completedCount = 0;

  // Count completed lessons and calculate weighted progress
  userLessons.forEach(lesson => {
    // Count completed lessons
    if (lesson.completedAt) {
      completedCount++;
    }

    // Calculate lesson progress based on type
    let lessonProgress = 0;

    if (lesson.lessonType === LessonType.VIDEO || lesson.lessonType === LessonType.QUIZ) {
      // For videos and quiz, use the progress field directly (0-100)
      lessonProgress = lesson.progress;
    } else if (lesson.lessonType === LessonType.GAME) {
      // For game, it's binary: completed (100%) or not (0%)
      lessonProgress = lesson.completedAt ? 100 : 0;
    }

    // Add this lesson's weighted progress contribution
    totalProgress += (lessonProgress / 100) * lessonWeight;
  });

  // Round to nearest integer
  const overallProgress = Math.round(totalProgress);

  return {
    progress: overallProgress,
    lessonsCompleted: completedCount
  };
}
